import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Loader2, Paperclip, Scale, Send, Sparkles, Pencil, X } from 'lucide-react';
import { jobsApi, documentsApi } from '../../services/api';
import { FrameChatMessage, FrameQuestionResponse } from '../../types';
import { detectLanguage, LANGUAGE_LABELS } from '../../utils/detectLanguage';
import { MicButton } from '../ui/MicButton';
import { CasePicker } from '../cases/CasePicker';

interface FramingChatProps {
  /** Called when the user accepts the framed question. */
  onAccept: (framed: {
    question: string;
    jurisdiction?: string;
    law_domain?: string;
    input_document_id?: string;
    /** Memo language detected from the user's typed words ('en'|'hi'|'ml'). */
    output_language?: string;
  }) => void;
  isSubmitting: boolean;
  /** Auto-sent as the first message (e.g. "deepen into research" from a draft). */
  prefill?: string;
  /** Case the research will be filed under — shown on the framed card (§4.3). */
  matterId?: string | null;
  /** When provided, the framed card renders a "File under: [case ▾] · Skip" row. */
  onMatterChange?: (matterId: string | null) => void;
  /** Document pre-attached before mount ("Research this document", §4.5) —
      rides frame-question turns and the created job exactly like a 📎 attach. */
  initialDoc?: { id: string; name: string } | null;
  /** Stretch to the parent's height (global chat pane) instead of the fixed
      34rem intake-page height. Purely presentational. */
  fill?: boolean;
}

const OPENER =
  "Tell me what's on your mind — a dispute, a notice you received, a clause you're unsure about. " +
  "Plain words are fine, any language. You can attach a document (📎) and I'll read it too. " +
  "I'll help you turn it into a precise research question.";

/** Trilingual example chips (§4.3) — one per supported memo language. */
const STARTERS = [
  'Research anticipatory bail after a 498A FIR',
  'मकान मालिक मेरी सिक्योरिटी डिपॉज़िट वापस नहीं कर रहा है',
  'എനിക്ക് കിട്ടിയ ചെക്ക് ബൗൺസായി — എന്ത് ചെയ്യണം?',
];

export function FramingChat({ onAccept, isSubmitting, prefill, matterId, onMatterChange, initialDoc, fill = false }: FramingChatProps) {
  const [messages, setMessages] = useState<FrameChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState('');
  const [framed, setFramed] = useState<FrameQuestionResponse | null>(null);
  const [docId, setDocId] = useState<string | null>(initialDoc?.id ?? null);
  const [docName, setDocName] = useState<string | null>(initialDoc?.name ?? null);
  const [isUploading, setIsUploading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const prefillSentRef = useRef(false);

  // Auto-send the prefill once, as the opening message (StrictMode-safe via ref).
  useEffect(() => {
    if (prefill && !prefillSentRef.current && messages.length === 0) {
      prefillSentRef.current = true;
      send(prefill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isThinking, framed, docName]);

  // Auto-grow the composer so wrapped placeholder / typed text is never clipped,
  // at any screen width. Caps at max-h (128px), then scrolls. Resets after send.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 128)}px`; // +2 = border-box borders
  }, [input, docName]);

  const onDrop = useCallback(async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setError('');
    setIsUploading(true);
    try {
      const { data } = await documentsApi.uploadDocument(file);
      setDocId(data.id);
      setDocName(file.name);
    } catch {
      setError('Could not upload that file. Please try again, or continue without it.');
    } finally {
      setIsUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // one upload rule everywhere: 50MB (spec §4.5)
  });

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || isThinking) return;
    setError('');
    setFramed(null);
    const history: FrameChatMessage[] = [...messages, { role: 'user', content }];
    setMessages(history);
    setInput('');
    setIsThinking(true);
    try {
      const { data } = await jobsApi.frameQuestion(history, docId || undefined);
      setMessages([...history, { role: 'assistant', content: data.reply }]);
      if (data.ready && data.question) setFramed(data);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
        'The assistant is unavailable right now — please try again in a moment.',
      );
      setMessages(history.slice(0, -1));
      setInput(content);
    } finally {
      setIsThinking(false);
    }
  };

  // Escape hatch: a user who already knows their question can run it as written
  // without waiting for the assistant to "frame" it. Prefer what they're typing;
  // fall back to their last sent message so they can bail out of a conversation.
  const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
  const runText = (input.trim() || lastUserMsg).trim();
  const canRunAsIs = !framed && !isThinking && !isSubmitting && runText.length > 0;

  // The memo is written in the language the user TYPED in. The framed question is
  // always English, so detect off the user's own words (all of them, joined).
  const userTypedText = messages.filter((m) => m.role === 'user').map((m) => m.content).join(' ');
  const framedLang = detectLanguage(userTypedText || lastUserMsg);

  const runAsIs = () => {
    if (!runText) return;
    onAccept({
      question: runText,
      input_document_id: docId || undefined,
      output_language: detectLanguage(runText),
    });
  };

  return (
    <div className={`flex flex-col ${fill ? 'h-full' : 'h-[34rem]'}`}>
      {/* thread */}
      <div className="flex-1 overflow-y-auto px-1 py-2 space-y-4">
        {/* assistant opener */}
        <Bubble role="assistant">{OPENER}</Bubble>

        {messages.map((m, i) => (
          <Bubble key={i} role={m.role}>{m.content}</Bubble>
        ))}

        {isThinking && (
          <Bubble role="assistant">
            <span className="inline-flex items-center gap-2 text-legal-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…
            </span>
          </Bubble>
        )}

        {/* framed-question card */}
        {framed?.question && (
          <div className="ml-4 sm:ml-10 border border-accent/40 bg-accent-50/70 rounded-2xl p-4 animate-slide-up">
            <p className="text-[11px] uppercase tracking-[0.18em] text-accent-700 mb-2 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Your question, ready to research.
            </p>
            <p className="font-display text-[15px] text-legal-900 leading-snug break-words">{framed.question_display || framed.question}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {[framed.jurisdiction, framed.law_domain].filter(Boolean).map(tag => (
                <span key={tag} className="text-[11px] text-legal-600 bg-white border border-legal-200 rounded-full px-2.5 py-0.5">
                  {tag}
                </span>
              ))}
            </div>
            {framedLang !== 'en' && (
              <p className="text-[11px] text-legal-500 mt-2.5">
                Memo will be written in <span className="font-semibold text-legal-700">{LANGUAGE_LABELS[framedLang]}</span>.
              </p>
            )}
            {/* File under a case — the decision lives on the framed card, never before typing (§4.3) */}
            {onMatterChange && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-[11px] text-legal-500">File under:</span>
                <CasePicker compact value={matterId ?? null} onChange={onMatterChange} />
                <button
                  type="button"
                  data-track="framing-file-under-skip"
                  onClick={() => onMatterChange(null)}
                  className="text-[11px] text-legal-400 hover:text-legal-600 transition-colors"
                >
                  · Skip
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => onAccept({
                  question: framed.question!,
                  jurisdiction: framed.jurisdiction || undefined,
                  law_domain: framed.law_domain || undefined,
                  input_document_id: docId || undefined,
                  output_language: framedLang,
                })}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-50"
              >
                {isSubmitting
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</>
                  : <><Scale className="h-4 w-4" /> Run this research</>}
              </button>
              <button
                type="button"
                onClick={() => setFramed(null)}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-legal-300 text-legal-600 rounded-lg text-sm hover:border-accent hover:text-accent-700 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Keep refining
              </button>
            </div>
          </div>
        )}

        {error && (
          <p className="ml-4 sm:ml-10 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">{error}</p>
        )}
        <div ref={endRef} />
      </div>

      {/* composer */}
      <div {...getRootProps()} className="mt-3 border-t border-legal-100 pt-3">
        <input {...getInputProps()} />

        {/* trilingual example chips — empty-history state only (§4.3) */}
        {messages.length === 0 && (
          <div className="mb-2.5 flex flex-wrap gap-2">
            {STARTERS.map((s) => (
              <button
                key={s}
                type="button"
                data-track="framing-example-chip"
                onClick={() => send(s)}
                className="text-xs text-legal-500 border border-legal-200 rounded-full px-3 py-1.5 hover:border-accent hover:text-accent-700 hover:bg-accent-50 transition-colors text-left"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* attached-document chip */}
        {docName && (
          <div className="mb-2 flex items-center justify-between gap-2 px-3 py-2 bg-accent-50/70 border border-accent/30 rounded-xl">
            <span className="flex items-center gap-2 min-w-0 text-sm text-legal-700">
              <Paperclip className="h-3.5 w-3.5 text-accent-700 flex-shrink-0" />
              <span className="truncate">{docName}</span>
            </span>
            <button
              type="button"
              onClick={() => { setDocId(null); setDocName(null); }}
              aria-label="Remove attached document"
              className="text-legal-400 hover:text-legal-700 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          data-tour="framing-composer"
          className="flex items-end gap-2"
        >
          <button
            type="button"
            onClick={open}
            disabled={isUploading}
            data-tour="framing-attach"
            aria-label="Attach a document"
            title="Attach a document (PDF, DOCX, TXT — max 50MB)"
            className="p-2.5 border border-legal-300 text-legal-500 rounded-xl hover:border-accent hover:text-accent-700 transition-colors disabled:opacity-40"
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <MicButton
            track="mic-framing"
            onFinal={(t) => setInput((v) => (v ? `${v} ` : '') + t)}
          />
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            placeholder={docName ? 'Ask about your document…' : 'Describe your situation…'}
            className="flex-1 resize-none px-3.5 py-2.5 border border-legal-300 rounded-xl text-sm focus:outline-none focus:ring-accent focus:border-accent max-h-32"
          />
          <button
            type="submit"
            disabled={!input.trim() || isThinking}
            aria-label="Send"
            className="p-2.5 bg-legal-900 text-accent rounded-xl hover:bg-legal-800 transition-colors disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>

        {/* Escape hatch, promoted to a real secondary button (§4.3) — one obvious
            path, everything available. Runs the question as written, no framing. */}
        {canRunAsIs && (
          <button
            type="button"
            data-track="research-run-as-written"
            data-tour="run-as-written"
            onClick={runAsIs}
            disabled={isSubmitting}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-legal-300 rounded-xl text-sm font-medium text-legal-700 hover:border-accent hover:text-accent-700 hover:bg-accent-50 transition-colors disabled:opacity-50"
          >
            Research it exactly as you wrote it →
          </button>
        )}
      </div>
    </div>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: React.ReactNode }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-legal-900 text-legal-100 rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap break-words">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-7 w-7 rounded-full bg-accent-100 border border-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Scale className="h-3.5 w-3.5 text-accent-700" />
      </div>
      <div className="min-w-0 max-w-[85%] bg-legal-50 border border-legal-100 rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-legal-800 whitespace-pre-wrap break-words">
        {children}
      </div>
    </div>
  );
}
